import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import api from "../lib/api";
import { Palette, Plus, Pencil, Trash2, Loader2, Sparkles, ChevronDown, Upload, FileText, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const COLORS = ["#6366F1", "#0EA5E9", "#E4405F", "#10B981", "#F59E0B", "#8B5CF6"];
const inputCls = "w-full bg-slate-950/60 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/60 transition-colors";

const DEFAULT_COLORS = {
  background: "#050807", surface: "#0B100E", accent: "#A8FF1A", accent_bright: "#39FF14",
  accent_soft: "#C8FF8A", text: "#F5F7F4", text_muted: "#9AA39D", text_subtle: "#5D6661",
  border: "#263129",
};

const DEFAULT_GUIDELINES = {
  colors: DEFAULT_COLORS, headline_font: "Space Grotesk", body_font: "Inter",
  personality: "", visual_direction: "", image_rules: "", typography_rules: "",
  avoid: "", tone_of_voice: "", content_philosophy: "", carousel_structure: "",
  logo_base64: "", logo_position: "top-left", logo_scale: 12, logo_opacity: 100,
  logo_margin: 4, footer_text: "", banned_words: [], blocked_topics: [],
};

const LOGO_POSITIONS = [
  ["top-left", "Top left"], ["top-right", "Top right"],
  ["bottom-left", "Bottom left"], ["bottom-right", "Bottom right"],
  ["bottom-center", "Bottom centre"],
];

const empty = {
  name: "", category: "", tone: "", instructions: "", color: "#6366F1",
  guidelines: DEFAULT_GUIDELINES,
};

const COLOR_FIELDS = [
  ["background", "Background"], ["surface", "Surfaces / cards"], ["accent", "Primary accent"],
  ["accent_bright", "Bright accent"], ["accent_soft", "Soft accent"], ["text", "Primary text"],
  ["text_muted", "Supporting text"], ["text_subtle", "Metadata"], ["border", "Borders"],
];

/* Each of these is injected verbatim into the prompts for both the copy and
   the imagery, which is what keeps a brand's creatives consistent. */
const GUIDELINE_FIELDS = [
  ["personality", "Brand personality", "Who the brand is, and who it is not."],
  ["visual_direction", "Visual direction", "The overall look: aesthetic, composition, colour balance."],
  ["image_rules", "Image rules", "What imagery must show, which materials, which metaphors."],
  ["typography_rules", "Headline treatment", "Length, capitalisation, which words get the accent colour."],
  ["tone_of_voice", "Tone of voice", "How the writing sounds. Include phrasing to prefer and avoid."],
  ["content_philosophy", "Content philosophy", "What every post should connect and argue."],
  ["carousel_structure", "Carousel structure", "The slide-by-slide shape of a carousel."],
  ["avoid", "Never use", "Banned imagery, clichés and phrasings."],
];

/* Lists the gate enforces, one entry per line. Banned words hold a post
   that uses them; blocked topics are never written about at all. */
const LIST_FIELDS = [
  ["banned_words", "Banned words and claims", "A post using any of these is held for you. One per line, e.g. revolutionary, guaranteed results."],
  ["blocked_topics", "Blocked topics", "Stories about these are skipped before anything is written: competitors, keywords, sensitive subjects. One per line."],
];

export const BrandsView = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [presets, setPresets] = useState([]);
  const [showGuides, setShowGuides] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const load = async () => { setLoading(true); const { data } = await api.get("/brands"); setBrands(data); setLoading(false); };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/brand-presets").then(({ data }) => setPresets(data.presets || [])).catch(() => {});
  }, []);

  const open = (b) => {
    setEditing(b || "new");
    // Brands saved before guidelines existed have none; fill the defaults in.
    setForm(b ? { ...empty, ...b, guidelines: { ...DEFAULT_GUIDELINES, ...(b.guidelines || {}),
      colors: { ...DEFAULT_COLORS, ...((b.guidelines || {}).colors || {}) } } } : empty);
    setShowGuides(Boolean(b?.guidelines?.visual_direction));
  };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));
  const setGuide = (k, v) => setForm((c) => ({ ...c, guidelines: { ...c.guidelines, [k]: v } }));
  const setColor = (k, v) => setForm((c) => ({
    ...c, guidelines: { ...c.guidelines, colors: { ...c.guidelines.colors, [k]: v } },
  }));

  /* Read a brand guideline document and fill the fields from it, rather than
     making someone retype a long document into ten boxes. */
  const uploadGuidelines = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setExtracting(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/brands/extract-guidelines", body, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setForm((c) => ({
        ...c,
        guidelines: {
          ...c.guidelines,
          ...data.guidelines,
          // Only overwrite the colours the document actually specified.
          colors: { ...c.guidelines.colors, ...(data.guidelines.colors || {}) },
        },
      }));
      setShowGuides(true);
      const n = (data.filled_fields || []).length;
      const c = Object.keys(data.guidelines.colors || {}).length;
      toast.success(`Read ${file.name} — filled ${n} field${n === 1 ? "" : "s"}${c ? ` and ${c} colours` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that document", { duration: 10000 });
    } finally {
      setExtracting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const uploadLogo = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/brands/logo", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setGuide("logo_base64", data.logo_base64);
      toast.success(`Logo added (${Math.round(data.bytes / 1024)} KB)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that image");
    } finally {
      setUploadingLogo(false);
      if (logoRef.current) logoRef.current.value = "";
    }
  };

  const applyPreset = async (id) => {
    try {
      const { data } = await api.get(`/brand-presets/${id}`);
      setForm((c) => ({ ...c, guidelines: { ...c.guidelines, ...data.guidelines } }));
      setShowGuides(true);
      toast.success("Guidelines loaded — edit anything you like");
    } catch { toast.error("Could not load that preset"); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Brand name required");
    setSaving(true);
    try {
      const payload = {
        name: form.name, category: form.category, tone: form.tone,
        instructions: form.instructions, color: form.color,
        guidelines: {
          ...form.guidelines,
          ...Object.fromEntries(LIST_FIELDS.map(([k]) => [k, (form.guidelines[k] || []).map((w) => w.trim()).filter(Boolean)])),
        },
      };
      if (editing === "new") await api.post("/brands", payload);
      else await api.put(`/brands/${editing.id}`, payload);
      toast.success("Brand saved");
      setEditing(null); await load();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/brands/${id}`); toast("Brand deleted"); await load(); };

  return (
    <div className="space-y-6" data-testid="brands-view">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold tracking-wider uppercase text-indigo-400 mb-2">Brand Voices</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-50">Brands</h1>
          <p className="text-slate-400 text-sm mt-2">Define a tone & instructions per brand so every account posts in its own voice.</p>
        </div>
        <button data-testid="new-brand-btn" onClick={() => open(null)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold active:scale-[0.98] transition-all">
          <Plus className="w-4 h-4" /> New brand
        </button>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div> : (
        brands.length === 0 ? (
          <div className="glass rounded-2xl p-12 flex flex-col items-center gap-3 text-slate-500">
            <Palette className="w-10 h-10" /><p className="text-sm">No brands yet. Create your first brand voice.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {brands.map((b) => (
              <motion.div key={b.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="glass rounded-2xl p-5 relative overflow-hidden" data-testid="brand-card">
                <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full blur-2xl opacity-20" style={{ background: b.color }} />
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center font-heading font-bold text-white text-lg" style={{ background: b.color }}>
                    {b.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex gap-1">
                    <button data-testid="edit-brand-btn" onClick={() => open(b)} className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid="delete-brand-btn" onClick={() => del(b.id)} className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-rose-500/20 hover:text-rose-300 flex items-center justify-center text-slate-300"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <p className="font-heading font-bold text-slate-100 mt-4">{b.name}</p>
                {b.category && <p className="text-xs text-indigo-300 font-medium">{b.category}</p>}
                {b.tone && <p className="text-xs text-slate-400 mt-2 line-clamp-2"><span className="text-slate-500">Tone:</span> {b.tone}</p>}
                {b.instructions && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{b.instructions}</p>}
              </motion.div>
            ))}
          </div>
        )
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing === "new" ? "New brand" : "Edit brand"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="brand-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Brand name" className={inputCls} />
            <input data-testid="brand-category" value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="Category (e.g. B2B SaaS, Fitness, Fashion)" className={inputCls} />
            <input data-testid="brand-tone" value={form.tone} onChange={(e) => set("tone", e.target.value)} placeholder="Tone / voice (e.g. Confident, witty, no jargon)" className={inputCls} />
            <textarea data-testid="brand-instructions" value={form.instructions} onChange={(e) => set("instructions", e.target.value)} rows={3} placeholder="Instructions (dos & don'ts, key phrases, emoji policy...)" className={`${inputCls} resize-none`} />
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">Label color</span>
              {COLORS.map((c) => (
                <button key={c} data-testid={`brand-color-${c.slice(1)}`} onClick={() => set("color", c)}
                  className={`w-7 h-7 rounded-full transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-offset-slate-900 ring-white scale-110" : ""}`} style={{ background: c }} />
              ))}
            </div>

            {/* Visual & voice guidelines */}
            <div className="pt-2 border-t border-slate-800">
              <button
                type="button"
                data-testid="toggle-guidelines"
                onClick={() => setShowGuides((v) => !v)}
                className="flex items-center gap-2 w-full text-left py-2"
              >
                <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${showGuides ? "" : "-rotate-90"}`} />
                <span className="text-sm font-semibold text-slate-200">Visual &amp; voice guidelines</span>
                <span className="text-[11px] text-slate-500 ml-auto">
                  Applied to every caption and image for this brand
                </span>
              </button>

              {showGuides && (
                <div className="space-y-4 pt-2">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 mb-2">Import from a document</p>
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".docx,.pdf,.txt,.md,.markdown"
                      data-testid="brand-guideline-upload"
                      onChange={(e) => uploadGuidelines(e.target.files)}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={extracting}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-slate-700 hover:border-indigo-500/60 text-slate-300 hover:text-slate-100 transition-colors disabled:opacity-60"
                    >
                      {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      <span className="text-xs font-semibold">
                        {extracting ? "Reading the document…" : "Upload a brand guideline document"}
                      </span>
                    </button>
                    <p className="flex items-center gap-1.5 text-[11px] text-slate-600 mt-1.5">
                      <FileText className="w-3 h-3" />
                      .docx, .pdf, .txt or .md — the palette, fonts and every rule below are
                      filled in from it, using the document&apos;s own wording.
                    </p>
                  </div>

                  {presets.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-400 mb-2">Or start from a preset</p>
                      <div className="flex flex-wrap gap-2">
                        {presets.map((pr) => (
                          <button
                            key={pr.id}
                            type="button"
                            data-testid={`preset-${pr.id}`}
                            onClick={() => applyPreset(pr.id)}
                            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-700 hover:border-indigo-500/60 transition-colors"
                          >
                            <span className="flex gap-1">
                              {["background", "accent", "text"].map((k) => (
                                <span key={k} className="w-3 h-3 rounded-full border border-slate-700"
                                  style={{ background: pr.colors[k] }} />
                              ))}
                            </span>
                            <span className="text-xs font-semibold text-slate-200">{pr.name}</span>
                            <Sparkles className="w-3 h-3 text-indigo-400" />
                          </button>
                        ))}
                      </div>
                      <p className="text-[11px] text-slate-600 mt-1.5">
                        Fills everything below. Edit any of it afterwards.
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="text-xs font-semibold text-slate-400 mb-1">Logo</p>
                    <p className="text-[11px] text-slate-600 mb-2">
                      Stamped onto every generated image after it is rendered — image models
                      cannot draw a logo accurately, so this is placed exactly, every time.
                      PNG with transparency works best.
                    </p>
                    <input ref={logoRef} type="file" accept="image/*" className="hidden"
                      data-testid="brand-logo-upload" onChange={(e) => uploadLogo(e.target.files)} />
                    <div className="flex items-center gap-3">
                      <div className="w-20 h-20 rounded-xl bg-slate-950/60 border border-slate-800 flex items-center justify-center overflow-hidden shrink-0"
                        style={{ background: form.guidelines.colors.background }}>
                        {form.guidelines.logo_base64 ? (
                          <img src={`data:image/png;base64,${form.guidelines.logo_base64}`} alt="" className="max-w-[80%] max-h-[80%] object-contain" />
                        ) : (
                          <ImagePlus className="w-5 h-5 text-slate-700" />
                        )}
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex gap-2">
                          <button type="button" onClick={() => logoRef.current?.click()} disabled={uploadingLogo}
                            className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 flex items-center gap-1.5 disabled:opacity-60">
                            {uploadingLogo ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                            {form.guidelines.logo_base64 ? "Replace" : "Upload logo"}
                          </button>
                          {form.guidelines.logo_base64 && (
                            <button type="button" onClick={() => setGuide("logo_base64", "")}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:text-rose-400">
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {LOGO_POSITIONS.map(([v, l]) => (
                            <button key={v} type="button" onClick={() => setGuide("logo_position", v)}
                              className={`px-2 py-1 rounded-md text-[10px] font-semibold border transition-colors ${
                                form.guidelines.logo_position === v
                                  ? "bg-indigo-500/15 border-indigo-500/50 text-indigo-200"
                                  : "border-slate-700 text-slate-500 hover:text-slate-300"}`}>
                              {l}
                            </button>
                          ))}
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-2 flex-1">
                            <span className="text-[10px] text-slate-500 w-10">Size</span>
                            <input type="range" min={4} max={30} value={form.guidelines.logo_scale}
                              onChange={(e) => setGuide("logo_scale", Number(e.target.value))} className="flex-1 accent-indigo-500" />
                            <span className="text-[10px] text-slate-500 w-8 font-mono">{form.guidelines.logo_scale}%</span>
                          </label>
                          <label className="flex items-center gap-2 flex-1">
                            <span className="text-[10px] text-slate-500 w-10">Opacity</span>
                            <input type="range" min={10} max={100} value={form.guidelines.logo_opacity}
                              onChange={(e) => setGuide("logo_opacity", Number(e.target.value))} className="flex-1 accent-indigo-500" />
                            <span className="text-[10px] text-slate-500 w-8 font-mono">{form.guidelines.logo_opacity}%</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-slate-400 mb-1">Footer text</p>
                    <p className="text-[11px] text-slate-600 mb-1.5">
                      The small line on every frame. Leave empty to use the brand name.
                    </p>
                    <input data-testid="brand-footer-text" value={form.guidelines.footer_text}
                      onChange={(e) => setGuide("footer_text", e.target.value)}
                      placeholder={form.name || "MAKE MONEY ONLY"} className={inputCls} />
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-slate-400 mb-2">Palette</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {COLOR_FIELDS.map(([key, label]) => (
                        <label key={key} className="flex items-center gap-2 bg-slate-950/60 border border-slate-800 rounded-lg px-2 py-1.5">
                          <input
                            type="color"
                            data-testid={`brand-palette-${key}`}
                            value={form.guidelines.colors[key] || "#000000"}
                            onChange={(e) => setColor(key, e.target.value)}
                            className="w-6 h-6 rounded cursor-pointer bg-transparent border-0 p-0"
                          />
                          <span className="min-w-0">
                            <span className="block text-[11px] text-slate-300 truncate">{label}</span>
                            <span className="block text-[10px] text-slate-600 font-mono">{form.guidelines.colors[key]}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-xs font-semibold text-slate-400 mb-1.5">Headline font</p>
                      <input data-testid="brand-headline-font" value={form.guidelines.headline_font}
                        onChange={(e) => setGuide("headline_font", e.target.value)}
                        placeholder="Space Grotesk" className={inputCls} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-400 mb-1.5">Body font</p>
                      <input data-testid="brand-body-font" value={form.guidelines.body_font}
                        onChange={(e) => setGuide("body_font", e.target.value)}
                        placeholder="Inter" className={inputCls} />
                    </div>
                  </div>

                  {LIST_FIELDS.map(([key, label, hint]) => (
                    <div key={key}>
                      <p className="text-xs font-semibold text-slate-400 mb-1">{label}</p>
                      <p className="text-[11px] text-slate-600 mb-1.5">{hint}</p>
                      <textarea
                        data-testid={`brand-${key}`}
                        value={(form.guidelines[key] || []).join("\n")}
                        onChange={(e) => setGuide(key, e.target.value.split("\n"))}
                        rows={3}
                        className={`${inputCls} resize-none text-xs leading-relaxed font-mono`}
                      />
                    </div>
                  ))}
                  {GUIDELINE_FIELDS.map(([key, label, hint]) => (
                    <div key={key}>
                      <p className="text-xs font-semibold text-slate-400 mb-1">{label}</p>
                      <p className="text-[11px] text-slate-600 mb-1.5">{hint}</p>
                      <textarea
                        data-testid={`brand-${key}`}
                        value={form.guidelines[key] || ""}
                        onChange={(e) => setGuide(key, e.target.value)}
                        rows={3}
                        className={`${inputCls} resize-none text-xs leading-relaxed`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-slate-200">Cancel</button>
            <button data-testid="save-brand-btn" onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save brand
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
